import React, { useState } from 'react';

import { RcFile } from 'antd/lib/upload';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/lib/upload/interface';
import { Avatar, message, Slider, Typography, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';

import { useClient } from 'src/contexts';

import styles from './ImageCropUploader.module.css';
import {
  UploadedMediaApiResponseType,
  UploadedMediaType,
} from '../MediaUploader/types';
import { displayFilePreview } from './utils';

const { Text } = Typography;

type ImageCropUploaderProps = {
  defaultValue?: Array<UploadFile>;
  id?: string;
  onUpload: (mediaIdList: Array<UploadedMediaType>) => void;
  pictureListType: 'picture-card' | 'picture';
  placeholder?: string;
  aspect?: number;
  cropShape?: 'rect' | 'round';
};

type ImageCropType = {
  uid: string;
  url?: string;
};

type ImageType = Array<ImageCropType> | null;

export const ImageCropUploader = ({
  id,
  onUpload,
  pictureListType,
  placeholder,
  defaultValue,
  aspect = 1,
  cropShape = 'round',
}: ImageCropUploaderProps) => {
  const [image, setImage] = useState<RcFile | null>();
  const [canRemove, setCanRemove] = useState<boolean>(false);
  const [imageValue, setImageValue] = useState<ImageType>(defaultValue || null);

  const { get, getToken, deletion } = useClient();

  const renderFilePreview = (image: RcFile) => (
    <div className={`${styles.mediaContainer} ${styles.dash}`}>
      {
        // @ts-ignore
        <Avatar size={80} src={image} className={styles.image} />
      }
      <DeleteOutlined
        id="remove-icon"
        className={styles.removeIcon}
        onClick={e => handleRemove(e, image.uid)}
      />
    </div>
  );

  const displayUploadedFile = (file: RcFile) => {
    return displayFilePreview(file, dataUri => {
      setImage(dataUri);
      handleUpload(file);
    });
  };

  const uploadFileToServer = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${process.env.REACT_APP_MR_CLIENT_API_URL}/medias/upload`,
      {
        body: formData,
        headers: {
          Authorization: `Bearer ${getToken().accessToken}`,
        },
        method: 'post',
        redirect: 'follow',
      }
    );

    return response.headers.get('Location')?.split('/')?.pop()!;
  };

  const handleUpload = async (file: File) => {
    if (file) {
      const mediaId = await uploadFileToServer(file);
      mediaId &&
        get(`/medias/${mediaId}`, (media: UploadedMediaApiResponseType) => {
          media.data &&
            onUpload([
              {
                id: media.data.id,
                name: media.data.name,
                type: media.data.mime,
                url: media.data.url.original,
              },
            ]);
        });
    }
  };

  const handleRemove = (e: any, mediaId: string) => {
    e.stopPropagation();
    imageValue && imageValue[0]
      ? deletion(
          `/medias/${mediaId}`,
          {},
          () => {
            setCanRemove(true);
            setImageValue(null);
          },
          () => {
            message.error(
              'Une erreur est survenue lors de suppression de la photo ou du document'
            );
          }
        )
      : setImage(null);
  };

  return (
    <div className={styles.uploadeContainer}>
      {placeholder && pictureListType === 'picture-card' && (
        <Text className={styles.placeholder} strong={true}>
          {placeholder}
        </Text>
      )}
      {image ? (
        renderFilePreview(image)
      ) : (
        <>
          <ImgCrop shape={cropShape} aspect={aspect} rotate={true}>
            {
              <Upload
                action={'https://www.mocky.io/v2/5cc8019d300000980a055e76'}
                beforeUpload={displayUploadedFile}
                className={styles.uploadButton}
                data-testid="image-uploader"
                id={id || 'image-uploader'}
                listType={pictureListType}
                showUploadList={false}
                // @ts-ignore
                data={handleUpload}
              >
                {!canRemove && imageValue && imageValue[0] ? (
                  <div className={styles.mediaContainer}>
                    <Avatar
                      size={80}
                      src={imageValue[0].url}
                      className={styles.image}
                    />
                    <DeleteOutlined
                      id="remove-icon"
                      className={styles.removeIcon}
                      onClick={e => handleRemove(e, imageValue[0].uid)}
                    />
                  </div>
                ) : (
                  <PlusOutlined id={`${id}-plus-icon`} />
                )}
              </Upload>
            }
          </ImgCrop>
          <Slider style={{ display: 'none' }} />
        </>
      )}
    </div>
  );
};
